import {
  isSupabaseConfigured,
  supabase,
  supabaseAnonKey,
  supabaseUrl,
} from "../supabase";

function requireSupabase() {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      "Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY."
    );
  }
  return supabase;
}

function maybeThrow(error) {
  if (error) {
    throw error;
  }
}

async function getFunctionErrorMessage(error) {
  if (!error) return "Edge Function request failed.";
  const fallback = error.message || "Edge Function request failed.";
  const response = error.context;

  if (!response || typeof response.clone !== "function") {
    return fallback;
  }

  try {
    const cloned = response.clone();
    const contentType = cloned.headers?.get?.("content-type") || "";

    if (contentType.includes("application/json")) {
      const body = await cloned.json();
      return body?.error || body?.message || fallback;
    }

    const text = (await cloned.text())?.trim();
    return text || fallback;
  } catch {
    return fallback;
  }
}

async function getCurrentAccessToken(client) {
  const {
    data: { session },
    error,
  } = await client.auth.getSession();

  if (error) {
    throw error;
  }

  return session?.access_token || null;
}

async function readFunctionResponse(response) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = (await response.text())?.trim();
  return text ? { message: text } : {};
}

async function getReadyAccessToken(client) {
  let token = await getCurrentAccessToken(client);
  if (token) return token;

  const { data, error } = await client.auth.refreshSession();
  if (error) {
    return null;
  }

  token = data?.session?.access_token || null;
  return token || null;
}

function mapProfileRow(row) {
  if (!row) return null;
  return {
    displayName: row.display_name || "",
    photoURL: row.photo_url || "",
    totalXP: Number(row.total_xp || 0),
    level: Number(row.level || 1),
    currentTitle: row.current_title || "Weakest Hunter of Mankind",
    autoBossQuest: !!row.auto_boss_quest,
    lastLevelUpPopupLevel: Number(row.last_level_up_popup_level || 0),
    lastLevelUpShownAt: row.last_level_up_shown_at || null,
    wakeTime: row.wake_time || "",
    sleepTime: row.sleep_time || "",
    timeZone: row.timezone || "UTC",
    longTermGoal: row.long_term_goal || "",
    hunterProfile: row.hunter_profile || "",
    questsPerDay: Math.max(1, Number(row.quests_per_day || 5)),
    preferredFocusMinutes: Number.isFinite(Number(row.preferred_focus_minutes))
      ? Number(row.preferred_focus_minutes)
      : null,
    nonNegotiables: row.non_negotiables || "",
    frictionPoints: row.friction_points || "",
    onboardingCompleted: row.onboarding_completed !== false,
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null,
  };
}

function mapBossQuestRow(row) {
  if (!row) return null;
  return {
    ...row,
    id: row.id || null,
    xp: Number(row.xp || 0),
    status: row.status || "pending",
    claimed: !!row.claimed,
    checklist: Array.isArray(row.checklist) ? row.checklist : row.checklist || [],
    createdAt: row.createdAt || null,
    updatedAt: row.updatedAt || null,
  };
}

async function invokeSystemFunction(name, body = {}) {
  const client = requireSupabase();
  const invokeWithToken = async (token) => {
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error("Supabase function transport is not configured.");
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const payload = await readFunctionResponse(response);
    return {
      data: response.ok ? payload : null,
      error: response.ok
        ? null
        : new Error(payload?.error || payload?.message || `Function request failed with ${response.status}`),
    };
  };

  let token = await getReadyAccessToken(client);
  if (!token) {
    throw new Error("System session is not ready yet.");
  }

  let { data, error } = await invokeWithToken(token);

  if (error) {
    let detailedMessage = await getFunctionErrorMessage(error);

    if (/invalid jwt/i.test(detailedMessage)) {
      const { data: refreshedData, error: refreshError } = await client.auth.refreshSession();
      if (!refreshError) {
        token = refreshedData?.session?.access_token || (await getCurrentAccessToken(client));
        if (token) {
          const retry = await invokeWithToken(token);
          data = retry.data;
          error = retry.error;
          if (!error) {
            return data;
          }
          detailedMessage = await getFunctionErrorMessage(error);
        }
      }
    }

    const nextError = new Error(detailedMessage);
    nextError.name = error.name || "FunctionsHttpError";
    nextError.cause = error;
    throw nextError;
  }

  return data;
}

export async function claimDailyQuestReward(questDate, questId) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("claim_daily_quest", {
    p_quest_date: questDate,
    p_quest_id: questId,
  });
  maybeThrow(error);

  return {
    quests: Array.isArray(data?.quests) ? data.quests : [],
    profile: mapProfileRow(data?.profile || null),
  };
}

export async function startDurationQuest(questDate, questId) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("start_duration_quest", {
    p_quest_date: questDate,
    p_quest_id: questId,
  });
  maybeThrow(error);

  return {
    quests: Array.isArray(data?.quests) ? data.quests : [],
  };
}

export async function unclaimDailyQuestReward(questDate, questId) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("unclaim_daily_quest", {
    p_quest_date: questDate,
    p_quest_id: questId,
  });
  maybeThrow(error);

  return {
    quests: Array.isArray(data?.quests) ? data.quests : [],
    profile: mapProfileRow(data?.profile || null),
  };
}

export async function claimBossQuestReward(weekId) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("claim_boss_quest", {
    p_week_id: weekId,
  });
  maybeThrow(error);

  return {
    bossQuest: mapBossQuestRow(data?.bossQuest || null),
    profile: mapProfileRow(data?.profile || null),
  };
}

export async function initializeSystemDay({ today, yesterday, announceEvents = true } = {}) {
  const data = await invokeSystemFunction("system-day-init", {
    today,
    yesterday,
    announceEvents,
  });

  return {
    today: data?.today || null,
    yesterday: data?.yesterday || null,
    quests: Array.isArray(data?.quests) ? data.quests : [],
    events: Array.isArray(data?.events) ? data.events : [],
    pendingPenaltyQuest: data?.pendingPenaltyQuest || null,
    profile: mapProfileRow(data?.profile || null),
  };
}

export async function orchestrateBossQuest({
  mode = "history",
  goal = "",
  userLevel = 1,
} = {}) {
  const data = await invokeSystemFunction("system-boss-quest", {
    mode,
    goal,
    userLevel,
  });

  return {
    status: data?.status || "noop",
    bossQuest: mapBossQuestRow(data?.bossQuest || null),
    message: data?.message || "",
  };
}
